import { logEvent } from "firebase/analytics";
import mixpanel from "mixpanel-browser";
import { CONSENT_CHANGE_EVENT, hasAnalyticsConsent } from "./consent";
import { getAnalyticsInstance } from "./firebase";
import { isInternalTraffic, syncInternalTrafficFromUrl } from "./internalTraffic";

type Properties = Record<string, string | number | boolean | null | undefined>;
const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN?.trim();
let mixpanelInitialized = false;

function getMixpanel() {
  if (!MIXPANEL_TOKEN || !hasAnalyticsConsent()) return null;
  if (!mixpanelInitialized) {
    mixpanel.init(MIXPANEL_TOKEN, {
      api_host: "https://api-js.mixpanel.com",
      autocapture: false,
      track_pageview: false,
      // Cookies can be shared by runmark.app and app.runmark.app; localStorage
      // is origin-scoped and would split the anonymous pre-signup journey.
      persistence: "cookie",
      cross_subdomain_cookie: true,
      secure_cookie: true,
      ip: false,
    });
    if (isInternalTraffic()) mixpanel.register({ traffic_type: "internal" });
    mixpanelInitialized = true;
  }
  return mixpanel;
}

export function initializeAnalyticsConsentListener() {
  if (typeof window === "undefined") return;
  syncInternalTrafficFromUrl();
  getAnalyticsInstance();
  window.addEventListener(CONSENT_CHANGE_EVENT, () => {
    if (hasAnalyticsConsent()) getMixpanel();
    else if (mixpanelInitialized) {
      mixpanel.opt_out_tracking(
        { clear_persistence: true } as Parameters<typeof mixpanel.opt_out_tracking>[0]
      );
    }
  });
}

export function trackEvent(name: string, properties: Properties = {}) {
  const analytics = getAnalyticsInstance();
  if (analytics) logEvent(analytics, name, properties);
  getMixpanel()?.track(name, properties);
}

export function trackPageView(path: string, title?: string) {
  const pageUrl = new URL(path, window.location.origin);
  const safePath = pageUrl.pathname;
  trackEvent("page_view", {
    page_path: safePath,
    page_title: title ?? document.title,
    page_location: `${window.location.origin}${safePath}`,
  });
}
